import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/site";
import { CITIES } from "@/lib/cities";

// NOTE: Canonical/og:url/JSON-LD URLs intentionally use the production
// host (https://901lots.com) — this is correct for SEO even when viewing
// the preview environment.
const SITE_URL = "https://901lots.com";
const DEFAULT_OG_IMAGE =
  "/img/hero-parking-lot.jpg";

/**
 * SEO meta + JSON-LD wrapper.
 * Drop this at the top of every page component.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title
    ? `${title} | ${SITE.name}`
    : `${SITE.name} — Parking Lot Striping & Sealcoating in Memphis & the Mid-South`;
  const canonical = `${SITE_URL}${path}`;

  // Always include a LocalBusiness root schema.
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: SITE.name,
    url: SITE_URL,
    email: SITE.email,
    telephone: SITE.phone,
    image,
    priceRange: "$$",
    description:
      "Professional parking lot striping, sealcoating, ADA compliance, and pavement marking services across Memphis and the Mid-South.",
    areaServed: CITIES.map((c) => ({
      "@type": "City",
      name: c.city,
      containedInPlace: { "@type": "State", name: c.stateName },
    })),
    address: {
      "@type": "PostalAddress",
      addressLocality: "Memphis",
      addressRegion: "TN",
      addressCountry: "US",
    },
    sameAs: [`https://${SITE.domain}`],
  };

  const schemas = [localBusinessSchema, ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta name="theme-color" content="#1D4ED8" />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
